import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Trash2, X, Info, CheckCircle, AlertCircle } from 'lucide-react';

/**
 * Modal - General purpose modal component
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - title: string
 *  - subtitle: string
 *  - size: 'sm' | 'md' | 'lg' | 'xl' | 'full'
 *  - children: ReactNode (modal body)
 *  - footer: ReactNode (custom footer)
 *  - closeOnOverlay: boolean (default true)
 *  - showCloseBtn: boolean (default true)
 *  - id: string
 *  - className: string
 */
export function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'md',
  children,
  footer,
  closeOnOverlay = true,
  showCloseBtn = true,
  id = 'modal',
  className = '',
}) {
  const overlayRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('form-is-open');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('form-is-open');
    };
  }, [isOpen]);

  useEffect(() => {
    const handler = (e) => { if (isOpen && e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (closeOnOverlay && e.target === overlayRef.current) onClose?.();
  };

  return createPortal(
    <div
      ref={overlayRef}
      className="ui-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? `${id}-title` : undefined}
      id={id}
    >
      <div className={`ui-modal ui-modal--${size} ${className}`}>
        {/* Header */}
        {(title || showCloseBtn) && (
          <div className="ui-modal-header">
            <div className="ui-modal-title-group">
              {title && <h2 className="ui-modal-title" id={`${id}-title`}>{title}</h2>}
              {subtitle && <p className="ui-modal-subtitle">{subtitle}</p>}
            </div>
            {showCloseBtn && (
              <button
                type="button"
                className="ui-modal-close-btn"
                onClick={onClose}
                aria-label="Close modal"
              >
                <X size={18} />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="ui-modal-body">{children}</div>

        {/* Footer */}
        {footer && <div className="ui-modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

/**
 * DeleteModal - Specialised delete confirmation modal
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - onConfirm: () => void
 *  - title: string
 *  - message: string
 *  - itemName: string (highlighted item being deleted)
 *  - confirmText: string (default 'Delete')
 *  - isLoading: boolean
 *  - id: string
 */
export function DeleteModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'Delete Confirmation',
  message = 'Are you sure you want to delete this record? This action cannot be undone.',
  itemName,
  confirmText = 'Delete',
  isLoading = false,
  id = 'delete-modal',
}) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('form-is-open');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('form-is-open');
    };
  }, [isOpen]);

  useEffect(() => {
    const handler = (e) => { if (isOpen && e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="ui-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${id}-title`}
      id={id}
    >
      <div className="ui-modal ui-modal--sm ui-delete-modal">
        {/* Icon */}
        <div className="ui-delete-modal-icon-wrap">
          <div className="ui-delete-modal-icon">
            <AlertTriangle size={28} />
          </div>
        </div>

        {/* Content */}
        <div className="ui-delete-modal-content">
          <h2 className="ui-delete-modal-title" id={`${id}-title`}>{title}</h2>
          <p className="ui-delete-modal-message">{message}</p>

          {itemName && (
            <div className="ui-delete-modal-item">
              <Trash2 size={14} />
              <span>{itemName}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="ui-delete-modal-footer">
          <button
            type="button"
            className="ui-modal-cancel-btn"
            onClick={onClose}
            disabled={isLoading}
            aria-label="Cancel deletion"
          >
            Cancel
          </button>
          <button
            type="button"
            className="ui-modal-confirm-btn"
            onClick={onConfirm}
            disabled={isLoading}
            aria-label={`Confirm ${confirmText}`}
            id={`${id}-confirm-btn`}
          >
            {isLoading ? (
              <>
                <span className="ui-modal-spinner" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 size={14} />
                {confirmText}
              </>
            )}
          </button>
        </div>

        {/* Close button */}
        <button
          type="button"
          className="ui-modal-close-btn ui-delete-modal-x"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>
    </div>,
    document.body
  );
}

/**
 * AlertModal - Information / success / error / warning modal
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - onConfirm: () => void
 *  - type: 'info' | 'success' | 'warning' | 'error'
 *  - title: string
 *  - message: string
 *  - confirmText: string
 *  - cancelText: string
 *  - id: string
 */
export function AlertModal({
  isOpen,
  onClose,
  onConfirm,
  type = 'info',
  title = 'Alert',
  message,
  confirmText = 'OK',
  cancelText,
  id = 'alert-modal',
}) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('form-is-open');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('form-is-open');
    };
  }, [isOpen]);

  useEffect(() => {
    const handler = (e) => { if (isOpen && e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const iconMap = {
    info: <Info size={26} />,
    success: <CheckCircle size={26} />,
    warning: <AlertTriangle size={26} />,
    error: <AlertCircle size={26} />,
  };

  return createPortal(
    <div
      className="ui-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={`${id}-title`}
      id={id}
    >
      <div className={`ui-modal ui-modal--sm ui-alert-modal ui-alert-modal--${type}`}>
        <div className={`ui-alert-modal-icon`}>{iconMap[type]}</div>
        <h2 className="ui-alert-modal-title" id={`${id}-title`}>{title}</h2>
        {message && <p className="ui-alert-modal-message">{message}</p>}
        <div className="ui-alert-modal-footer">
          {cancelText && (
            <button type="button" className="ui-modal-cancel-btn" onClick={onClose}>
              {cancelText}
            </button>
          )}
          <button
            type="button"
            className={`ui-alert-modal-confirm ui-alert-btn--${type}`}
            onClick={onConfirm || onClose}
            id={`${id}-confirm-btn`}
          >
            {confirmText}
          </button>
        </div>
        <button type="button" className="ui-modal-close-btn ui-alert-modal-x" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
      </div>
    </div>,
    document.body
  );
}

export default DeleteModal;
